import type { KitGroup, WizardLine } from '../types/wizard';
import { normalizeTagId, type KitBomItem } from './tagAllocation';
import { componentRequiredQty, effectiveNonScanQty, lineFilledQty } from './lineQuantities';

/** Unique RFID tags allocated to document lines (excludes raw/unmatched scanner reads). */
export function countAllocatedDocumentTags(
  lines: WizardLine[],
  kitGroups: KitGroup[]
): number {
  const seen = new Set<string>();
  const sourceLines = kitGroups.length > 0 ? kitGroups.flatMap((g) => g.lines) : lines;
  for (const line of sourceLines) {
    if (line.isKitHeader) continue;
    for (const tag of line.rfidCodes) {
      const t = normalizeTagId(tag);
      if (t) seen.add(t);
    }
  }
  return seen.size;
}

export type LineScanProgress = {
  line: WizardLine;
  kitIndex: number;
  required: number;
  /** RFID tag count */
  scanned: number;
  /** Manual or auto non-RFID quantity */
  nonScan: number;
  /** scanned + nonScan (dispatch total for this line) */
  filled: number;
  status: 'pending' | 'in_progress' | 'complete';
};

export type ScanSessionStats = {
  totalTags: number;
  totalRequired: number;
  totalScanned: number;
  overallProgress: number;
  linesComplete: number;
  linesInProgress: number;
  lineProgress: LineScanProgress[];
};

/** Lines that must reach warehouse qty before submit (Scannable === 'X'). */
export function getScannableLineEntries(
  lines: WizardLine[],
  kitGroups: KitGroup[]
): { line: WizardLine; kitIndex: number }[] {
  if (kitGroups.length > 0) {
    return kitGroups.flatMap((g, ki) =>
      g.lines
        .filter((l) => l.scannable && !l.isKitHeader)
        .map((line) => ({ line, kitIndex: ki }))
    );
  }
  return lines
    .filter((l) => l.scannable && !l.isKitHeader)
    .map((line) => ({ line, kitIndex: 0 }));
}

/** All material lines shown during scan (legacy kit table shows every component). */
export function getDisplayLineEntries(
  lines: WizardLine[],
  kitGroups: KitGroup[]
): { line: WizardLine; kitIndex: number }[] {
  if (kitGroups.length > 0) {
    return kitGroups.flatMap((g, ki) =>
      g.lines
        .filter((l) => !l.isKitHeader && l.materialCode.trim())
        .map((line) => ({ line, kitIndex: ki }))
    );
  }
  return lines
    .filter((l) => !l.isKitHeader && l.materialCode.trim())
    .map((line) => ({ line, kitIndex: 0 }));
}

export function computeScanSessionStats(
  lines: WizardLine[],
  kitGroups: KitGroup[],
  bom: KitBomItem[] | null,
  tagSet: Set<string>,
  /** When set, line buckets and progress reflect only this kit tab. */
  activeKitIndex?: number
): ScanSessionStats {
  let entries = getDisplayLineEntries(lines, kitGroups);
  if (kitGroups.length > 0 && activeKitIndex != null) {
    entries = entries.filter((e) => e.kitIndex === activeKitIndex);
  }
  let totalRequired = 0;
  let totalScanned = 0;
  let linesComplete = 0;
  let linesInProgress = 0;

  const lineProgress: LineScanProgress[] = entries.map(({ line, kitIndex }) => {
    const required = componentRequiredQty(line, kitGroups, kitIndex, bom);
    const scanned = line.scanQty;
    const nonScan = effectiveNonScanQty(line, required);
    const filled = lineFilledQty(line, required);
    totalRequired += required;
    totalScanned += filled;

    let status: LineScanProgress['status'] = 'pending';
    if (filled >= required && required > 0) {
      status = 'complete';
      linesComplete += 1;
    } else if (filled > 0 || scanned > 0) {
      status = 'in_progress';
      linesInProgress += 1;
    }

    return { line, kitIndex, required, scanned, nonScan, filled, status };
  });

  const overallProgress =
    totalRequired > 0 ? Math.min(1, totalScanned / totalRequired) : 0;

  return {
    totalTags: countAllocatedDocumentTags(lines, kitGroups),
    totalRequired,
    totalScanned,
    overallProgress,
    linesComplete,
    linesInProgress,
    lineProgress,
  };
}

export type KitReviewSection = {
  kitMaterial: string;
  kitDesc: string;
  kitQty: number;
  kitIndex: number;
  components: LineScanProgress[];
};

function lineScanProgress(
  line: WizardLine,
  kitIndex: number,
  kitGroups: KitGroup[],
  bom: KitBomItem[] | null
): LineScanProgress {
  const required = componentRequiredQty(line, kitGroups, kitIndex, bom);
  const scanned = line.scanQty;
  const nonScan = effectiveNonScanQty(line, required);
  const filled = lineFilledQty(line, required);
  let status: LineScanProgress['status'] = 'pending';
  if (filled >= required && required > 0) status = 'complete';
  else if (filled > 0 || scanned > 0) status = 'in_progress';
  return { line, kitIndex, required, scanned, nonScan, filled, status };
}

/** Confirm screen: one section per kit, only scannable component lines (no kit header row). */
export function getKitWiseReviewSections(
  kitGroups: KitGroup[],
  bom: KitBomItem[] | null
): KitReviewSection[] {
  return kitGroups.map((g, kitIndex) => ({
    kitMaterial: g.kitMaterial,
    kitDesc: g.kitDesc,
    kitQty: g.kitQty,
    kitIndex,
    components: g.lines
      .filter((l) => !l.isKitHeader && l.materialCode.trim())
      .map((line) => lineScanProgress(line, kitIndex, kitGroups, bom)),
  }));
}

/** Flat documents or fallback list without kit grouping. */
export function getReviewLineProgress(
  lines: WizardLine[],
  kitGroups: KitGroup[],
  bom: KitBomItem[] | null,
  tagSet: Set<string>
): LineScanProgress[] {
  if (kitGroups.length > 0) {
    return getKitWiseReviewSections(kitGroups, bom).flatMap((s) => s.components);
  }
  return computeScanSessionStats(lines, kitGroups, bom, tagSet).lineProgress;
}
